import React, { useEffect, useMemo, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase-config';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { FiUpload, FiFileText, FiZap, FiLoader } from 'react-icons/fi';
import {
  createThread,
  createMessage,
  createRunNoStream,
  getRunStatus,
  listMessages,
  titleToAssistantIDMap,
} from '../chat/openAIUtils';
import { readFileContent } from '../utils/fileUtils';

import Flashcard from './Flashcard';

const ASSISTANT_TITLE = 'Flashcard Generator';

function shortId() {
  return Math.random().toString(36).slice(2, 8);
}

function slugify(text) {
  return (text || '').toString().toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function isArabic(text) {
  if (!text) return false;
  const arabicRange = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
  return arabicRange.test(text);
}

const FlashcardGenerator = () => {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [tags, setTags] = useState('');
  const [languageMode, setLanguageMode] = useState('mixed');
  const [inputMethod, setInputMethod] = useState('paste'); // 'paste' | 'upload'
  const [notes, setNotes] = useState('');
  const [numCards, setNumCards] = useState(12);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');

  const [threadId, setThreadId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [deckPayload, setDeckPayload] = useState(null);

  const assistantId = useMemo(() => titleToAssistantIDMap[ASSISTANT_TITLE], []);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    setLoading(true);
    setError('');

    try {
      const content = await readFileContent(file);
      setFileContent(content);
      setNotes(content);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const buildPrompt = () => {
    const instructions = `You are an assistant that ONLY replies with a single JSON object that conforms exactly to the schema below and contains no extra commentary. Do not include markdown fences.

SCHEMA:
{
  "deckId": "string",
  "title": "string",
  "subject": "string",
  "languageMode": "ar|en|mixed",
  "tags": ["array of relevant tags"],
  "cards": [
    {
      "term": "string",
      "explanation": "string",
      "translation": "string",
      "transliteration": "optional string (include ONLY if the term or translation is Arabic)",
      "example": "optional string"
    }
  ]
}

CRITICAL REQUIREMENTS:
- Generate EXACTLY ${numCards} high-quality flashcard cards
- Language mode must be: ${languageMode}
- If Arabic text is present (term or translation), include a transliteration; otherwise omit that field
- Use the provided lesson notes to extract or derive terms
- Ensure valid JSON (no trailing commas, no code fences)
- Titles should be concise and human-friendly
- Tags should be relevant keywords for categorization
`;

    const content = `Title: ${title || 'Untitled Deck'}\nLanguage Mode: ${languageMode}\nNumber of Cards Required: ${numCards}\nTags: ${tags || 'Islamic Studies, Education'}\n---\nLesson Notes:\n${notes}`;

    return `${instructions}\n\n${content}`;
  };

  async function generate() {
    if (!notes.trim()) {
      setError('Please provide some content to generate flashcards from.');
      return;
    }

    setError('');
    setLoading(true);
    setDeckPayload(null);
    try {
      const thread = await createThread(ASSISTANT_TITLE);
      setThreadId(thread.id);
      await createMessage(thread.id, buildPrompt(), ASSISTANT_TITLE);
      const run = await createRunNoStream(thread.id, assistantId, ASSISTANT_TITLE);

      // poll
      let status = run;
      let attempts = 0;
      while (attempts < 30) {
        const s = await getRunStatus(thread.id, run.id, ASSISTANT_TITLE);
        if (s.status === 'completed') {
          break;
        }
        if (s.status === 'failed' || s.status === 'cancelled' || s.status === 'expired') {
          throw new Error(`Run ended with status: ${s.status}`);
        }
        await new Promise((r) => setTimeout(r, 1500));
        attempts++;
      }

      const msgs = await listMessages(thread.id, ASSISTANT_TITLE);
      const assistantMsg = (msgs?.data || []).find((m) => m.role === 'assistant') || (msgs?.data || [])[0];
      const parts = assistantMsg?.content || [];
      const textPart = parts.find((p) => p.type === 'text') || parts[0];
      const raw = textPart?.text?.value || textPart?.value || '';

      if (!raw) throw new Error('Empty assistant response');

      let parsed = null;
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        // try to fix common JSON issues
        const fixed = raw.replace(/```json|```/g, '').replace(/,\s*([}\]])/g, '$1');
        parsed = JSON.parse(fixed);
      }

      // enforce transliteration rule: remove transliteration if not Arabic
      if (parsed?.cards?.length) {
        parsed.cards = parsed.cards.map((c) => {
          const hasArabic = isArabic(c.term) || isArabic(c.translation);
          if (!hasArabic) {
            const { transliteration, ...rest } = c;
            return rest;
          }
          return c;
        });
      }

      // fill in top-level if missing
      parsed.deckId = parsed.deckId || `${slugify(title || 'deck')}-${shortId()}`;
      parsed.title = parsed.title || (title || 'Untitled Deck');
      parsed.subject = parsed.subject || 'Islamic Studies';
      parsed.languageMode = parsed.languageMode || languageMode;
      parsed.tags = parsed.tags || (tags ? tags.split(',').map(t => t.trim()) : ['Islamic Studies']);

      setDeckPayload(parsed);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to generate');
    } finally {
      setLoading(false);
    }
  }

  async function saveDeck() {
    if (!deckPayload) return;
    const deckId = deckPayload.deckId || `${slugify(title || 'deck')}-${shortId()}`;
    
    try {
      // Save to flashcardLibrary collection
      const libraryDocRef = doc(db, 'flashcardLibrary', deckId);
      const metadata = {
        deck: JSON.stringify(deckPayload), // Stringified deck data
        tags: deckPayload.tags || [], // Tags from the deck
        ownerID: user ? user.uid : 'anonymous', // Owner user ID or anonymous
        createdAt: new Date().toISOString(),
        lastEdited: new Date().toISOString(),
        public: false
      };

      await setDoc(libraryDocRef, metadata);

      // Save to user's personal collection
      if (user) {
        const userDeckRef = doc(db, 'users', user.uid, 'flashcards', deckId);
        await setDoc(userDeckRef, {
          deckId: deckId,
          addedAt: new Date().toISOString(),
          isOwner: true
        });
      }

      navigate(`/flashcards/${deckId}`);
    } catch (e) {
      console.error(e);
      setError('Failed to save deck');
    }
  }

  return (
    <div className="ai-generator-container">
      {/* Header */}
      <div className="ai-header">
        <div className="ai-header-content">
          <div className="ai-title-section">
            <div className="ai-icon">
              <FiZap />
            </div>
            <h2 className="ai-title">Generate flashcard set</h2>
          </div>
          <p className="ai-subtitle">Upload your notes and we'll create flashcards automatically</p>
        </div>
      </div>

      {/* Input Method Selection */}
      <div className="input-method-tabs">
        <button
          className={`input-tab ${inputMethod === 'paste' ? 'active' : ''}`}
          onClick={() => setInputMethod('paste')}
        >
          <FiFileText />
          Paste text
        </button>
        <button
          className={`input-tab ${inputMethod === 'upload' ? 'active' : ''}`}
          onClick={() => setInputMethod('upload')}
        >
          <FiUpload />
          Upload files
        </button>
      </div>

      {/* Input Area */}
      <div className="input-area">
        {inputMethod === 'paste' ? (
          <div className="paste-area">
            <textarea
              placeholder="Put your notes here. We'll do the rest."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="notes-textarea"
              rows={8}
            />
            <div className="char-counter">
              {notes.length}/100,000 characters
            </div>
          </div>
        ) : (
          <div className="upload-area">
            <input
              type="file"
              onChange={handleFileUpload}
              accept=".txt,.docx,.jpg,.jpeg,.png,.gif"
              className="file-input"
              id="file-upload"
            />
            <label htmlFor="file-upload" className="file-upload-label">
              <FiUpload />
              <span>Choose a file or drag it here</span>
              <small>Supports: .txt, .docx, .jpg, .jpeg, .png, .gif</small>
            </label>
            {uploadedFile && (
              <div className="file-info">
                <span>✓ {uploadedFile.name}</span>
                {fileContent && <span className="file-content-preview">Content loaded</span>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Generation Options */}
      <div className="generation-options">
        <div className="options-grid">
          <div className="option-group">
            <label>Number of Cards</label>
            <input
              type="number"
              min={3}
              max={50}
              value={numCards}
              onChange={(e) => setNumCards(Number(e.target.value))}
              className="option-input"
            />
          </div>
          <div className="option-group">
            <label>Tags (comma-separated)</label>
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="option-input"
              placeholder="Islamic Studies, Five Pillars, Core Beliefs"
            />
          </div>
          <div className="option-group">
            <label>Language Mode</label>
            <select
              value={languageMode}
              onChange={(e) => setLanguageMode(e.target.value)}
              className="option-input"
            >
              <option value="ar">Arabic</option>
              <option value="en">English</option>
              <option value="mixed">Mixed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <div className="generate-section">
        <button
          onClick={generate}
          disabled={loading || (!notes.trim() && !fileContent)}
          className="generate-button"
        >
          {loading ? (
            <>
              <FiLoader className="spinner" />
              Generating your flashcard set...
            </>
          ) : (
            'Generate'
          )}
        </button>
        {loading && (
          <p className="generation-note">
            This may take a while depending on the size of your upload
          </p>
        )}
      </div>

      {/* Disclaimer */}
      <div className="disclaimer">
        This product is enhanced by AI and may provide incorrect or problematic content. Do not enter personal data.
      </div>

      {/* Error Display */}
      {error && <div className="error-message">{error}</div>}

      {/* Preview */}
      {deckPayload && (
        <div className="preview-section">
          <div className="preview-header">
            <h3>Preview: {deckPayload.title}</h3>
            <p>{deckPayload.cards?.length || 0} cards generated</p>
          </div>
          <div className="preview-cards">
            {deckPayload.cards?.map((card, idx) => (
              <div key={idx} className="preview-card">
                <Flashcard flashcard={card} showNavigation={false} />
              </div>
            ))}
          </div>
          <div className="preview-actions">
            <button onClick={saveDeck} className="save-button">
              Save Deck
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlashcardGenerator; 